import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { Button, InputNumber, message, Tooltip } from "antd";
import cn from 'classnames';

import EditMeterPopup from "./EditMeterPopup";

import {
	EditIcon,
	ElectricityIcon,
	GasIcon,
	WaterDropIcon,
} from "../../../../../../resource/icons";
import actions from "../../../../store/actions/actions";
import { HeatIcon } from "../../../../../../resource/icons";
import "./meter.scss";
import { isDismantledMeterStatus } from '../../../../utils/isDismantledMeterStatus';

const IconMap = {
	1: <WaterDropIcon />,
	3: <ElectricityIcon />,
	2: <GasIcon />,
};

const Meter = ({ meter }) => {
	const dispatch = useDispatch();
	const [editPopupOpened, setEditPopupOpened] = useState(false);
	const [editButtonShown, setEditButtonShown] = useState(false);
	const [inputsValue, setInputsValue] = useState([]);
	const inputRef = useRef([]);
	const [currentValueEqualPrevValue, setCurrentValueEqualPrevValue] =
		useState(false);
	const [showInvalidValueError, setShowInvalidValueError] = useState(false);

	useEffect(() => {
		if (showInvalidValueError) {
			const messageTimeoutId = setTimeout(() => {
				message.error("Показания должны содержать только цифры");
			}, 200);
			const anotherMessageTimeoutId = setTimeout(() => {
				setShowInvalidValueError(false);
			}, 2000);
			return () => {
				clearTimeout(messageTimeoutId);
				clearTimeout(anotherMessageTimeoutId);
			};
		}
	}, [showInvalidValueError]);

	const lastReadingDate = meter.tariffList
		?.map((tariff) => tariff.meterLastReading)
		?.reduce((acc, meterLastReading) => {
			if (acc < meterLastReading) {
				return meterLastReading;
			} else {
				return acc;
			}
		}, "");
	const [valueIsLessThanPrev, setValueIsLessThanPrev] = useState({});
	const changedMeters = useSelector((store) => store.meterStore.changedMeters);

	const closeEditPopup = useCallback(() => setEditPopupOpened(false), []);

  const isDisabledStatus = isDismantledMeterStatus(meter.meterStatus);

	return (
		<div
      className={cn('Meter', {
        Meter__disabled: isDisabledStatus,
      })}
			key={meter.id}
			onMouseOver={() => setEditButtonShown(true)}
			onMouseLeave={() => setEditButtonShown(false)}
		>
			<div className="header">
				<div
          className={cn('meter-code', {
            'meter-code-water': meter.type.name === 'Вода',
            'meter-code-electricity': meter.type.name === 'Электричество',
            'meter-code-gas': meter.type.name === 'Газ',
          })}
				>
					{IconMap[meter.type.id]}
					<span>{meter.meterCode}</span>

					{
						<div className="space">
							{editButtonShown && (
								<EditIcon
									className="meter_action_button"
									onClick={() => setEditPopupOpened(true)}
								/>
							)}
						</div>
					}
					{editPopupOpened && (
						<EditMeterPopup
							onClose={closeEditPopup}
							meter={meter}
						/>
					)}
				</div>
				{lastReadingDate ? (
					<div className="last-reading">
						<span className="last-reading-content">
							Дата снятия:{" "}
							{moment(lastReadingDate).format(
								"DD.MM.YY"
							)}
						</span>
					</div>
				) : (
					<Button
						className="last-reading-empty"
						type="link"
						onClick={() => {
							for (const el of inputRef.current) {
								if (!el.inputNumberRef.currentValue) {
									el.focus();
									return;
								}
							}
						}}
					>
						Внесите показания
					</Button>
				)}
				{meter.tagList.map((tag) => {
					if (tag.id === 1) {
						return (
							<Tooltip title="Котельная" key={tag.id}>
								<HeatIcon style={{ height: 20 }} />
							</Tooltip>
						);
					}
				})}
			</div>
			<div className="inputs">
				{meter.tariffList.map((tariff, index) => {
					return (
						<div className="input-with-prev-value" key={tariff.id}>
							<InputNumber
                className={cn('input', {
                  'input-empty': currentValueEqualPrevValue,
                  'warning-input': valueIsLessThanPrev[tariff.id],
                  input__disabled: isDisabledStatus,
                })}
								ref={(ref) => {
									inputRef.current[index] = ref;
								}}
								placeholder={tariff.lastValue}
								value={isDisabledStatus ? tariff.lastValue : inputsValue[index]}
								onChange={(value) => {
									setInputsValue({ ...inputsValue, [index]: value });
									const valueExist = value || value === 0;
									const valueIsNumber = !isNaN(Number(value));
									if (!valueIsNumber) {
										setShowInvalidValueError(true);
										return;
									}
									if (valueExist) {
										setValueIsLessThanPrev({
											...valueIsLessThanPrev,
											[tariff.id]: Number(value) < tariff.lastValue,
										});
									} else {
										setValueIsLessThanPrev({
											...valueIsLessThanPrev,
											[tariff.id]: false,
										});
									}
									if (valueExist && Number(value) !== tariff.lastValue) {
										setCurrentValueEqualPrevValue(false);
										dispatch({
											type: actions.METER_CHANGED,
											payload: {
												tariffId: tariff.id,
												meterId: meter.id,
												value,
												meterCode: meter.meterCode,
												lastValue: tariff.lastValue,
												valueIsLessThanPrev: Number(value) < tariff.lastValue,
											},
										});
									} else {
										setCurrentValueEqualPrevValue(true);
										dispatch({
											type: actions.METER_CHANGED_TO_PREV_VALUE,
											payload: {
												tariffId: tariff.id,
												meterId: meter.id,
											},
										});
									}
								}}
								disabled={isDisabledStatus}
							/>
							<div className="tariff">{tariff.name}</div>
							<span className="prev-value">
								Предыдущий месяц:{" "}
								{tariff.lastMonthValue && tariff.lastMonthDate ? (
									<strong>
										{tariff.lastMonthValue} (
										{moment(tariff.lastMonthDate).format('DD.MM.YYYY')})
									</strong>
								) : (
									<span>данных нет</span>
								)}
							</span>
						</div>
					);
				})}
			</div>
			{meter.description && (
				<span className="description">{meter.description}</span>
			)}
			{Object.keys(changedMeters).find((key) =>
				key.includes(`${meter.id}`)
			) && (
				<div className="cancel_button_row">
					<Button
						type="link"
						onClick={() => {
							dispatch({
								type: actions.SAVED_METER_CHANGING,
							});
							setInputsValue([]);
							setValueIsLessThanPrev(
								Object.keys(valueIsLessThanPrev).reduce((acc, key) => {
									acc[key] = false;
									return acc;
								}, {})
							);
						}}
					>
						Отменить изменения
					</Button>
				</div>
			)}
		</div>
	);
};

export default Meter;
